import React, { useEffect, useState, useContext } from "react";
import useSWR from "swr";
import _ from "lodash";
import { useHistory, useLocation } from "react-router";
import { fetcher, logsFetcher, makeRequest } from "../../utils/request";
import constants from "../../config/constants";
import Footer from "../layout/Footer";
import RecentPosts from "../account/RecentPosts";
import RecentTopics from "../account/RecentTopics";
import Playlists from "../account/Playlists";
import LogService from "../../utils/logs";
import CurrentPlan from "../account/CurrentPlan";
import GraceBanner from "../landingpage/GraceBanner";
import { trackPageView } from "../../utils/adobeAnalytics";
import moment from "moment";
import { useQuery } from "../../hooks/useQuery";
import FlashMessage from "../common/FlashMessage";
import AppContext from "../AppContext";
import { pushDatatoAdobeLayer } from '../../analytics/pmiAdobeLog';

const swrOptions = {
  revalidateOnFocus: false,
};

const getContentKeys = (items) => {
  if (!items) return "";
  return items;
};

const AccountPage = ({
  isAuthenticated,
  freeUntil,
  myPlan,
  hasAccessUntil,
  gracePeriod,
  isMember,
  userPermission,
  userId,hasAccess
}) => {

    let subStatus = "";



  // Adobe Analytics
  const { isFirstRender, setIsFirstRender } = useContext(AppContext);
  useEffect(() => {
    if (!isFirstRender) {
      trackPageView({ eVar4: "account page" });
    } else {
      setIsFirstRender(false);
    }

  }, []);
  useEffect(() => {
 if (hasAccess && hasAccess == true) {
            if (isMember == true) {
                subStatus = "subscribed:pmi_membership"
            }
            else {
                subStatus = "subscribed:stdplus_subscription"
            }
        }
        else {
            subStatus = "Non-subscribed"
        }
    if (isAuthenticated) {
      let loginData = {
        event: 'loggedin',
        eventInfo: 'loggedin',
        user: {
          membershipTier: userPermission?.isMember?.membership,
          membershipStatus: userPermission?.isMember?.isMember == true ? 'active' : 'expired',
          pmiID: userId
        }
      }
      let subscriptionStatus = {
        event: "sd-app-load",
        eventInfo: "sd-app-load",
        sd: {
          renewalDate: hasAccessUntil.split('T')[0],
          subStatus: subStatus,
        }
      }
      pushDatatoAdobeLayer({ loginData, status: isAuthenticated, subscriptionStatus })
    }
  }, [isAuthenticated]);
  // end Adobe Analytics

  const history = useHistory();
  const location = useLocation();
  const [flashMessage, setFlashMessage] = useState("");
  const { data: userInfo } = useSWR("/api/user", fetcher, swrOptions);
  const { data: emailOptIn } = useSWR(
    "/api/user/email-optin",
    fetcher,
    swrOptions
  );
  const { data: playlists } = useSWR("/api/playlist/getallplaylists", fetcher);
  const { data: lookup } = useSWR("/api/lookup", fetcher, swrOptions);
  const { data: recentItems } = useSWR(
    ["/api/log/query", constants.logTypes.viewItem],
    logsFetcher,
    swrOptions
  );
  const { data: recentItemsTopics } = useSWR(
    ["/api/log/recentcontent", constants.logTypes.viewItem],
    logsFetcher,
    swrOptions
  );
  const [notificationPreferences, setNotificationPreferences] = useState(
    emailOptIn?.result
  );
  const [notificationPreferencesErr, setNotificationPreferencesErr] = useState(
    false
  );

  useEffect(() => {
    setNotificationPreferences(emailOptIn?.result);
  }, [emailOptIn]);

  const logs = getContentKeys(recentItemsTopics);

  useEffect(() => {
    if (!isAuthenticated) {
      history.push(`/login`);
    } else {
      LogService.logPageVisit({
        contentKey: constants.pageKeys.account,
        contentSubType: constants.contentSubTypes.page,
      });
    }
  }, []);

  if (!userInfo || !playlists || !lookup || !recentItems || !recentItemsTopics) {
    return (
      <div className="bg-white flex flex-col">
        <div
          className="container mx-auto p-4 text-center"
          style={{ marginTop: "90px" }}
        >
          <i className="fas fa-spin fa-spinner mr-3" />
          Loading ...
        </div>
      </div>
    );
  }

  const capitalize = (s) => {
    if (!s || typeof s !== "string") return "";
    return s.charAt(0).toUpperCase() + s.slice(1);
  };

  const userFullName = `${capitalize(userInfo.firstName)} ${capitalize(
    userInfo.lastName
  )}`;

  const handleEmailNotification = async (e) => {
    try {
      const isChecked = e.target?.checked;
      await makeRequest("post", "/api/user/email-optin", isChecked);
      setNotificationPreferences(isChecked);
      setNotificationPreferencesErr(false);
    } catch (err) {
      setNotificationPreferencesErr(true);
    }
  };

  const realEndDate = moment(hasAccessUntil)
    .subtract(gracePeriod, "days")
    .format("ll");

  const query = new URLSearchParams(location.search);
  if (query.get("message") === "feedback" && !flashMessage) {
    setFlashMessage("Thank you for your feedback.");
  }
  if (!query.get("message") && flashMessage) {
    setFlashMessage("");
  }

  return (
    <div className="flex flex-col">
      <div className="container mx-auto p-4" style={{ marginTop: "80px" }}>
        {flashMessage && (
          <FlashMessage
            message={flashMessage}
            onClose={() => history.push("/account")}
          />
        )}

        <div className="bg-white rounded border border-gray-400 my-10">
          <div className="flex justify-between flex-wrap lg:flex-no-wrap">
            <div className="flex py-5 px-10">
              <div className="border-2 border-gray-800 rounded-full h-12 w-12 md:h-24 md:w-24 flex items-center justify-center">
                <i className="fa fa-user md:text-5xl text-gray-400" />
              </div>
              {userInfo && userInfo.id && (
                <div className="flex flex-col justify-center ml-4 title-font">
                  <h3 className="capitalize text-gray-800 font-bold">
                    {userFullName}
                  </h3>
                  <p className="mb-2">{userInfo.email}</p>
                  <p className="mb-2">
                    <input
                      type="checkbox"
                      id="emailNotifications"
                      name="emailNotifications"
                      value={notificationPreferences}
                      checked={notificationPreferences}
                      onChange={handleEmailNotification}
                    />
                    <label htmlFor="emailNotifications">
                      {" "}
                      Send me email notifications
                    </label>
                  </p>
                  {notificationPreferencesErr && (
                    <p className="text-red-700">
                      There was an error with the server
                    </p>
                  )}
                </div>
              )}
            </div>

            <CurrentPlan
              myPlan={myPlan}
              freeUntil={freeUntil}
              hasAccessUntil={hasAccessUntil}
              gracePeriod={gracePeriod}
              isMember={isMember}
            />
          </div>
        </div>

        <GraceBanner
          hasAccessUntil={hasAccessUntil}
          gracePeriod={gracePeriod}
        />

        <div className="flex flex-col lg:flex-row">
          <RecentPosts
            playlists={playlists}
            recentItemsTopics={recentItemsTopics.recentContents}
            recentItemsLogs={recentItemsTopics.logs}
            userInfo={userInfo}
          />
          <RecentTopics recentTopics={recentItemsTopics.topics} />
        </div>

        {playlists && <Playlists playlists={playlists} />}
      </div>

      <Footer />
    </div>
  );
};

export default AccountPage;
